import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Spin, Button, Card, Row, Col, Alert, Tag, Radio, Select } from 'antd'
import { CSVLink } from 'react-csv'
import moment from 'moment'
import styled from 'styled-components'
import Table from '../../components/Table'
import * as twilioRecordingsServices from '../../apiServices/twilioRecordingsServices'
import { exportYearlyReport } from '../../utils/exportDoctorCallReport'

const Container = styled.div`
  width: 100%;
  padding: 20px;
`

const STAT_PADDING = { textAlign: 'center' }

/**
 * รายงานเวลาคุยกับแพทย์ (รายวัน)
 *
 * แหล่งเวลา: /TwilioRecordings (OMA) มี durationSec + dateCreated + roomSid + sourceSid (track SID)
 * แหล่ง map ผู้ป่วย: /bookingRooms (telemed) ของวันนั้น -> { sid (track SID), bookingId }
 * จับคู่: TwilioRecording.sourceSid === bookingRooms.sid -> bookingId -> Booking -> patient
 *
 * หมายเหตุ data bug: bookingRooms.bookingId ถูกเก็บมี quote ซ้อน ("\"..\"") -> ใช้ cleanId() ถอด
 */
const fetchAllRecordings = async (fromMs, toMs) => {
  return twilioRecordingsServices.fetchAllInRange({ dateFrom: fromMs, dateTo: toMs, pageSize: 500 })
}

const toSeconds = (v) => {
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : 0
}

const groupByRoom = (recordings) => {
  const map = {}
  recordings.forEach((rec) => {
    // จัดกลุ่มตามห้อง (roomSid) แต่เก็บ sourceSid (track SID) ของทุก track ไว้ join กับ bookingRooms.sid
    const roomKey = rec.roomSid || rec.sourceSid
    if (!roomKey) return
    const startMs = moment(rec.dateCreated).valueOf()
    const durSec = toSeconds(rec.durationSec)
    if (!map[roomKey]) {
      map[roomKey] = { roomSid: roomKey, startMs, durSec, sourceSids: new Set() }
    } else {
      map[roomKey].startMs = Math.min(map[roomKey].startMs, startMs || map[roomKey].startMs)
      // หลาย track ต่อ 1 ห้อง -> ใช้ค่าที่นานสุดเป็นความยาวสาย (ไม่ใช่ผลรวม)
      map[roomKey].durSec = Math.max(map[roomKey].durSec, durSec)
    }
    if (rec.sourceSid) map[roomKey].sourceSids.add(rec.sourceSid)
  })
  return map
}

// bookingRooms.bookingId / booking.id อาจมี quote หรือ space ครอบ -> ถอดออกให้เทียบกันได้
const cleanId = (v) => String(v == null ? '' : v).replace(/^[\s"']+|[\s"']+$/g, '')

// คืนชื่อ-นามสกุลจริงของผู้ป่วย ถ้าไม่มีคืน null (ใช้ filter "คัดเฉพาะคนที่มีชื่อ")
const realPatientName = (b) => {
  if (!b || !b.patient) return null
  const p = b.patient
  const full = (p.fullnameHIE || p.fullname || '').trim()
  if (full) return full
  const composed = [p.firstName, p.lastName].filter(Boolean).join(' ').trim()
  return composed || null
}

const secToMin = (sec) => Math.round((sec / 60) * 10) / 10

const ReportDoctorCallDuration = ({ bookings, BookingRoom, Booking }) => {
  const [yearNum, setYearNum] = useState(moment().year())
  const year = moment().year(yearNum).startOf('year')
  const range = [year.clone().startOf('year'), year.clone().endOf('year')]
  const yearOptions = (() => {
    const current = moment().year()
    const list = []
    for (let y = current + 1; y >= current - 10; y -= 1) list.push(y)
    return list
  })()
  const [groupBy, setGroupBy] = useState('month') // 'day' | 'month'
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [monthAgg, setMonthAgg] = useState({}) // { 'YYYY-MM': { totalRooms, totalSec } } - ทุกห้องไม่ว่าจะ match หรือไม่
  const [callDetails, setCallDetails] = useState([]) // ระดับต่อสาย สำหรับ Export XLSX
  const [diag, setDiag] = useState({ totalRooms: 0, matchedRooms: 0, bookingRooms: 0, noSync: false })
  // กัน race condition: เมื่อเปลี่ยนปีก่อน load เดิมจบ -> ทิ้งผลของ load ที่ outdated
  const loadIdRef = useRef(0)

  const load = useCallback(
    async (r) => {
      if (!r || !r[0] || !r[1]) return
      loadIdRef.current += 1
      const myLoadId = loadIdRef.current
      const isStale = () => myLoadId !== loadIdRef.current
      setLoading(true)
      try {
        const dayStart = r[0].clone().startOf('day')
        const dayEnd = r[1].clone().endOf('day')

        // 1) recordings ในช่วงวันที่ (OMA) -> group ตามห้อง พร้อมเก็บ sourceSids
        const recordings = await fetchAllRecordings(dayStart.valueOf(), dayEnd.valueOf())
        if (isStale()) return
        const roomMap = groupByRoom(recordings)

        // นับสายรายเดือน "ทุกห้อง" (ไม่ว่าจะ match ผู้ป่วยหรือไม่) สำหรับ summary
        const monthAggLocal = {}
        Object.values(roomMap).forEach((room) => {
          const mKey = moment(room.startMs).format('YYYY-MM')
          if (!monthAggLocal[mKey]) monthAggLocal[mKey] = { totalRooms: 0, totalSec: 0 }
          monthAggLocal[mKey].totalRooms += 1
          monthAggLocal[mKey].totalSec += room.durSec
        })

        // 2) bookingRooms ในช่วง (telemed, เผื่อ ±6 ชม. กัน timezone) -> sid(trackSid) -> bookingId
        const brFrom = dayStart.clone().subtract(6, 'hours').toISOString()
        const brTo = dayEnd.clone().add(6, 'hours').toISOString()
        const bookingRoomsOfDay = (await BookingRoom.listByDateRange(brFrom, brTo)) || []
        if (isStale()) return
        const sidToBookingId = {}
        bookingRoomsOfDay.forEach((br) => {
          if (br && br.sid && br.bookingId != null) sidToBookingId[br.sid] = cleanId(br.bookingId)
        })

        // 3) ห้อง -> bookingId ผ่าน sourceSid === bookingRooms.sid
        const roomToBookingId = {}
        Object.values(roomMap).forEach((room) => {
          const hit = [...room.sourceSids].find((s) => sidToBookingId[s])
          if (hit) roomToBookingId[room.roomSid] = sidToBookingId[hit]
        })

        // 4) โหลด Booking + patient ตาม bookingId (in-memory ก่อน ที่เหลือ fetch เป็น batch)
        const bookingIds = [...new Set(Object.values(roomToBookingId))]
        const bookingById = {}
        ;(bookings || []).forEach((b) => {
          if (b && b.id) bookingById[cleanId(b.id)] = b
        })
        const missing = bookingIds.filter((id) => !bookingById[id])
        for (let i = 0; i < missing.length; i += 50) {
          // eslint-disable-next-line no-await-in-loop
          const part = (await Booking.fetchBookingsByIds(missing.slice(i, i + 50))) || []
          if (isStale()) return
          part.forEach((b) => {
            if (b && b.id) bookingById[cleanId(b.id)] = b
          })
        }

        // 5) รวมเป็น ต่อผู้ป่วย ต่อหน่วยเวลา (วัน หรือ เดือน)
        const fmt = groupBy === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD'
        const buckets = {}
        let matched = 0
        Object.values(roomMap).forEach((room) => {
          const bid = roomToBookingId[room.roomSid]
          if (!bid) return
          const b = bookingById[bid]
          const pname = realPatientName(b)
          if (!pname) return // คัดเฉพาะที่มีชื่อ-นามสกุลจริง
          matched += 1
          const patientKey = (b && (b.patientId || (b.patient && b.patient.id))) || bid
          const bucketStr = moment(room.startMs).format(fmt)
          const key = `${patientKey}__${bucketStr}`
          if (!buckets[key]) {
            buckets[key] = {
              key,
              bucketStr,
              name: pname,
              doctor: (b.doctor && (b.doctor.fullname || b.doctor.fullnameHIE)) || '-',
              sessions: 0,
              totalSec: 0,
              startMs: room.startMs,
              endMs: room.startMs + room.durSec * 1000,
              daysSet: new Set(),
            }
          }
          const agg = buckets[key]
          agg.sessions += 1
          agg.totalSec += room.durSec
          agg.startMs = Math.min(agg.startMs, room.startMs)
          agg.endMs = Math.max(agg.endMs, room.startMs + room.durSec * 1000)
          agg.daysSet.add(moment(room.startMs).format('YYYY-MM-DD'))
        })

        const out = Object.values(buckets).map((r) => ({
          ...r,
          daysCount: r.daysSet.size,
          daysList: [...r.daysSet].sort().map((d) => moment(d, 'YYYY-MM-DD').format('D')).join(', '),
          avgSec: r.sessions ? r.totalSec / r.sessions : 0,
        }))

        if (isStale()) return

        // ระดับต่อสาย: คัดเฉพาะที่มี "ชื่อ-นามสกุล" ผู้ป่วยจริง (ไม่นับ booking id หรือ placeholder)
        const callDetailsLocal = []
        Object.values(roomMap).forEach((room) => {
          const bid = roomToBookingId[room.roomSid]
          if (!bid) return
          const b = bookingById[bid]
          const pname = realPatientName(b)
          if (!pname) return
          callDetailsLocal.push({
            startMs: room.startMs,
            durSec: room.durSec,
            matched: true,
            patientId: b.patientId || (b.patient && b.patient.id) || bid,
            patientName: pname,
            doctorName: (b.doctor && (b.doctor.fullname || b.doctor.fullnameHIE)) || null,
            roomSid: room.roomSid,
          })
        })
        setCallDetails(callDetailsLocal)

        setMonthAgg(monthAggLocal)
        setRows(
          out.sort((a, b) => a.bucketStr.localeCompare(b.bucketStr) || b.totalSec - a.totalSec),
        )
        setDiag({
          totalRooms: Object.keys(roomMap).length,
          matchedRooms: matched,
          bookingRooms: bookingRoomsOfDay.length,
          noSync: recordings.length === 0,
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('ReportDoctorCallDuration load error', e)
        if (isStale()) return
        setRows([])
        setMonthAgg({})
        setCallDetails([])
        setDiag({ totalRooms: 0, matchedRooms: 0, bookingRooms: 0, noSync: false })
      } finally {
        if (!isStale()) setLoading(false)
      }
    },
    [bookings, BookingRoom, Booking, groupBy],
  )

  useEffect(() => {
    load(range)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, groupBy, yearNum])

  const isMonth = groupBy === 'month'
  const dtFmt = 'DD/MM/YYYY HH:mm'

  // สรุปรายเดือนครบ 12 เดือนของปีที่เลือก (เดือนไม่มีข้อมูล = 0)
  // - "ทั้งหมด" มาจาก roomMap (ทุกห้องจาก Twilio ไม่ว่าจะจับคู่ผู้ป่วยได้หรือไม่)
  // - "ระบุผู้ป่วย" มาจาก rows (เฉพาะที่ join กับ bookingRooms.sid ได้)
  const monthlySummary = isMonth
    ? Array.from({ length: 12 }, (_, i) => {
      const monthKey = `${year.format('YYYY')}-${String(i + 1).padStart(2, '0')}`
      const all = monthAgg[monthKey] || { totalRooms: 0, totalSec: 0 }
      const monthRows = rows.filter((r) => r.bucketStr === monthKey)
      const distinctPatients = new Set(monthRows.map((r) => r.key.split('__')[0])).size
      const matchedSec = monthRows.reduce((s, r) => s + r.totalSec, 0)
      return {
        key: monthKey,
        monthLabel: moment(monthKey, 'YYYY-MM').format('MM/YYYY'),
        allRooms: all.totalRooms,
        allSec: all.totalSec,
        patients: distinctPatients,
        matchedSec,
        avgPerPatientSec: distinctPatients ? matchedSec / distinctPatients : 0,
      }
    })
    : []

  const summaryColumns = [
    { title: 'เดือน', dataIndex: 'monthLabel', key: 'monthLabel' },
    {
      title: 'จำนวนสายทั้งหมด',
      dataIndex: 'allRooms',
      key: 'allRooms',
      align: 'center',
      sorter: (a, b) => a.allRooms - b.allRooms,
      render: (v) => <b>{v}</b>,
    },
    {
      title: 'รวมเวลาทั้งหมด (นาที)',
      key: 'allMin',
      align: 'center',
      sorter: (a, b) => a.allSec - b.allSec,
      render: (r) => <b>{secToMin(r.allSec)}</b>,
    },
    { title: 'ผู้ป่วยที่ระบุได้', dataIndex: 'patients', key: 'patients', align: 'center', sorter: (a, b) => a.patients - b.patients },
    {
      title: 'เวลาคุย (ระบุได้, นาที)',
      key: 'matchedMin',
      align: 'center',
      sorter: (a, b) => a.matchedSec - b.matchedSec,
      render: (r) => secToMin(r.matchedSec),
    },
    {
      title: 'เฉลี่ย/ผู้ป่วย (นาที)',
      key: 'avgPerPatientMin',
      align: 'center',
      sorter: (a, b) => a.avgPerPatientSec - b.avgPerPatientSec,
      render: (r) => secToMin(r.avgPerPatientSec),
    },
  ]

  const summaryCsvHeaders = [
    { label: 'เดือน', key: 'monthLabel' },
    { label: 'จำนวนสายทั้งหมด', key: 'allRooms' },
    { label: 'รวมเวลาทั้งหมด (นาที)', key: 'allMin' },
    { label: 'ผู้ป่วยที่ระบุได้', key: 'patients' },
    { label: 'เวลาคุย (ระบุได้, นาที)', key: 'matchedMin' },
    { label: 'เฉลี่ย/ผู้ป่วย (นาที)', key: 'avgPerPatientMin' },
  ]
  const summaryCsvData = monthlySummary.map((m) => ({
    monthLabel: m.monthLabel,
    allRooms: m.allRooms,
    allMin: secToMin(m.allSec),
    patients: m.patients,
    matchedMin: secToMin(m.matchedSec),
    avgPerPatientMin: secToMin(m.avgPerPatientSec),
  }))

  const columns = [
    {
      title: isMonth ? 'เดือน' : 'วันที่',
      dataIndex: 'bucketStr',
      key: 'bucket',
      fixed: 'left',
      render: (v) => (isMonth ? moment(v, 'YYYY-MM').format('MM/YYYY') : moment(v, 'YYYY-MM-DD').format('DD/MM/YYYY')),
      sorter: (a, b) => a.bucketStr.localeCompare(b.bucketStr),
    },
    { title: 'ชื่อผู้ป่วย', dataIndex: 'name', key: 'name', fixed: 'left' },
    { title: 'แพทย์', dataIndex: 'doctor', key: 'doctor' },
    ...(isMonth
      ? [
        { title: 'จำนวนวันที่มีสาย', dataIndex: 'daysCount', key: 'daysCount', align: 'center', sorter: (a, b) => a.daysCount - b.daysCount },
        { title: 'วันที่มีสาย', dataIndex: 'daysList', key: 'daysList' },
      ]
      : []),
    { title: 'จำนวนสาย', dataIndex: 'sessions', key: 'sessions', align: 'center', sorter: (a, b) => a.sessions - b.sessions },
    {
      title: 'รวมเวลาคุย (นาที)',
      key: 'totalMin',
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.totalSec - b.totalSec,
      render: (r) => <b>{secToMin(r.totalSec)}</b>,
    },
    {
      title: 'เฉลี่ย/สาย (นาที)',
      key: 'avgMin',
      align: 'center',
      sorter: (a, b) => a.avgSec - b.avgSec,
      render: (r) => secToMin(r.avgSec),
    },
    { title: isMonth ? 'สายแรก' : 'เริ่มสายแรก', key: 'start', align: 'center', render: (r) => moment(r.startMs).format(dtFmt) },
    { title: isMonth ? 'สายสุดท้าย' : 'สิ้นสุดสายสุดท้าย', key: 'end', align: 'center', render: (r) => moment(r.endMs).format(dtFmt) },
  ]

  const csvHeaders = [
    { label: isMonth ? 'เดือน' : 'วันที่', key: 'bucket' },
    { label: 'ชื่อผู้ป่วย', key: 'name' },
    { label: 'แพทย์', key: 'doctor' },
    ...(isMonth
      ? [
        { label: 'จำนวนวันที่มีสาย', key: 'daysCount' },
        { label: 'วันที่มีสาย', key: 'daysList' },
      ]
      : []),
    { label: 'จำนวนสาย', key: 'sessions' },
    { label: 'รวมเวลาคุย (นาที)', key: 'totalMin' },
    { label: 'เฉลี่ย/สาย (นาที)', key: 'avgMin' },
    { label: isMonth ? 'สายแรก' : 'เริ่มสายแรก', key: 'start' },
    { label: isMonth ? 'สายสุดท้าย' : 'สิ้นสุดสายสุดท้าย', key: 'end' },
  ]
  const csvData = rows.map((r) => ({
    bucket: isMonth ? moment(r.bucketStr, 'YYYY-MM').format('MM/YYYY') : moment(r.bucketStr, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    name: r.name,
    doctor: r.doctor,
    daysCount: r.daysCount,
    daysList: r.daysList,
    sessions: r.sessions,
    totalMin: secToMin(r.totalSec),
    avgMin: secToMin(r.avgSec),
    start: moment(r.startMs).format(dtFmt),
    end: moment(r.endMs).format(dtFmt),
  }))

  return (
    <Container>
      <Row gutter={16} className="mb-3" align="middle">
        <Col>
          <Select
            value={yearNum}
            style={{ width: 120 }}
            onChange={(v) => {
              setYearNum(v)
              setRows([])
              setMonthAgg({})
              setCallDetails([])
            }}
          >
            {yearOptions.map((y) => (
              <Select.Option key={y} value={y}>{`ปี ${y}`}</Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Radio.Group value={groupBy} onChange={(e) => setGroupBy(e.target.value)} buttonStyle="solid">
            <Radio.Button value="day">ต่อวัน</Radio.Button>
            <Radio.Button value="month">ต่อเดือน</Radio.Button>
          </Radio.Group>
        </Col>
        <Col>
          <Button type="primary" onClick={() => load(range)} loading={loading}>
            โหลดข้อมูล
          </Button>
        </Col>
        <Col flex="auto" style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            disabled={!callDetails.length}
            onClick={() => exportYearlyReport(yearNum, callDetails)}
            style={{ marginRight: 8 }}
          >
            Export XLSX รายงานปี
          </Button>
          {isMonth && (
            <CSVLink
              data={summaryCsvData}
              headers={summaryCsvHeaders}
              filename={`สรุปรายเดือน_เวลาคุยกับแพทย์_ปี${year.format('YYYY')}.csv`}
              style={{ marginRight: 8 }}
            >
              <Button>Export สรุปรายเดือน (CSV)</Button>
            </CSVLink>
          )}
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`รายละเอียดเวลาคุยกับแพทย์_ปี${year.format('YYYY')}.csv`}
          >
            <Button disabled={!rows.length}>Export รายละเอียด (CSV)</Button>
          </CSVLink>
        </Col>
      </Row>

      <Row gutter={16} className="mb-3">
        <Col span={8}>
          <Card bodyStyle={STAT_PADDING}>
            <div>ผู้ป่วยที่คุยกับแพทย์ (ในช่วงที่เลือก)</div>
            <h2 style={{ margin: 0 }}>
              {new Set(rows.map((r) => r.key.split('__')[0])).size} คน
              <span style={{ fontSize: 14, color: '#888' }}> / {rows.length} {isMonth ? 'รายการ-เดือน' : 'รายการ-วัน'}</span>
            </h2>
          </Card>
        </Col>
        <Col span={8}>
          <Card bodyStyle={STAT_PADDING}>
            <div>รวมเวลาคุยทั้งหมด</div>
            <h2 style={{ margin: 0 }}>{secToMin(rows.reduce((s, r) => s + r.totalSec, 0))} นาที</h2>
          </Card>
        </Col>
        <Col span={8}>
          <Card bodyStyle={STAT_PADDING}>
            <div>จับคู่ห้องสายกับผู้ป่วยได้</div>
            <h2 style={{ margin: 0 }}>
              <Tag color={diag.matchedRooms === diag.totalRooms ? 'green' : 'orange'} style={{ fontSize: 16 }}>
                {diag.matchedRooms} / {diag.totalRooms}
              </Tag>
            </h2>
          </Card>
        </Col>
      </Row>

      {!loading && diag.noSync && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="ไม่มีข้อมูล recording ที่ sync จาก Twilio ในช่วงวันที่เลือก"
          description={
            <span>
              ตาราง TwilioRecording ใน OMA-backend ไม่มีระบบ sync อัตโนมัติ ต้องไปที่เมนู
              {' '}<b>Twilio Recordings</b> กดปุ่ม <b>Sync ทั้งหมดจาก Twilio</b> (หรือ Sync จาก Storage)
              {' '}ให้ครอบคลุมช่วงวันที่ต้องการก่อน แล้วจึงกลับมาดูรายงานนี้
            </span>
          }
        />
      )}

      {diag.totalRooms > 0 && diag.matchedRooms < diag.totalRooms && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={`จับคู่ผู้ป่วยได้ ${diag.matchedRooms} / ${diag.totalRooms} ห้อง (${Math.round((diag.matchedRooms / diag.totalRooms) * 100)}%)`}
          description={
            <span>
              ตัวเลข <b>นาที/ผู้ป่วย</b> ในตารางแม่นยำ (มาจาก Twilio durationSec จริง) แต่ <b>coverage</b> ไม่ครบเพราะ
              {' '}sourceSid ไม่ตรง bookingRooms.sid — สาเหตุที่พบบ่อย: bookingRooms ไม่ถูกสร้าง (สายขัดข้อง),
              {' '}sync ไม่ได้เก็บ source_sid, หรือสาย test ที่ไม่มี booking
              {' '}<br />
              <b>ใช้สำหรับ KPI:</b> ตัวเลขต่อผู้ป่วยที่ขึ้นในตารางใช้ได้ แต่ไม่ควรอ้าง "จำนวนสายทั้งหมด" จาก report นี้
              {' '}เพราะ Twilio retention และ sync coverage ไม่ครอบคลุมประวัติย้อนหลังทั้งหมด
            </span>
          }
        />
      )}

      {loading ? (
        <div className="w-100 d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
          <Spin size="large" tip="กำลังโหลดข้อมูล..." />
        </div>
      ) : (
        <>
          {isMonth && (
            <div className="mb-4">
              <h3>สรุปรายเดือน ปี {year.format('YYYY')}</h3>
              <Table
                rowKey="key"
                columns={summaryColumns}
                dataSource={monthlySummary}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          )}
          {isMonth && <h3>รายละเอียด (ผู้ป่วย × เดือน)</h3>}
          <Table rowKey="key" columns={columns} dataSource={rows} scroll={{ x: 'max-content' }} />
        </>
      )}
    </Container>
  )
}

export default ReportDoctorCallDuration
